import { useTranslation } from 'react-i18next';

import { type Translate, permissionGroupLabel, permissionLabel } from './i18n';
import styles from './Collab.module.css';

interface Props {
  /** group id -> permission ids (from the server's catalog). */
  groups: Record<string, string[]>;
  /** Permission ids to show (others are skipped: board scope hides workspace permissions). */
  scope?: (permission: string) => boolean;
  value: ReadonlySet<string>;
  onChange: (next: Set<string>) => void;
  /** Cannot be turned off. */
  required?: ReadonlySet<string>;
  /** Cannot be turned on (owner-only guarantees). */
  locked?: ReadonlySet<string>;
  /** The built-in template: differences are marked. */
  defaults?: ReadonlySet<string>;
  disabled?: boolean;
  /** Permissions the editor itself lacks may not be granted. */
  grantable?: ReadonlySet<string> | null;
}

/** Permissions grouped logically (never one flat list of checkboxes). */
export function PermissionChecklist({
  groups,
  scope,
  value,
  onChange,
  required,
  locked,
  defaults,
  disabled,
  grantable,
}: Props) {
  const { t } = useTranslation();
  const tr = t as unknown as Translate;
  const toggle = (permission: string, on: boolean) => {
    const next = new Set(value);
    if (on) next.add(permission);
    else next.delete(permission);
    onChange(next);
  };
  return (
    <div className={styles.matrix}>
      {Object.entries(groups).map(([group, permissions]) => {
        const shown = permissions.filter((p) => !scope || scope(p));
        if (shown.length === 0) return null;
        return (
          <fieldset key={group} className={styles.group}>
            <legend>{permissionGroupLabel(tr, group)}</legend>
            {shown.map((permission) => {
              const isOn = value.has(permission);
              const isLocked = locked?.has(permission) ?? false;
              const isRequired = required?.has(permission) ?? false;
              const cannotGrant = !isOn && grantable != null && !grantable.has(permission);
              const changed = defaults ? defaults.has(permission) !== isOn : false;
              return (
                <label
                  key={permission}
                  className={`${styles.permission} ${changed ? styles.changed : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={isOn}
                    disabled={disabled === true || isLocked || (isRequired && isOn) || cannotGrant}
                    onChange={(event) => {
                      toggle(permission, event.target.checked);
                    }}
                  />
                  <span>{permissionLabel(tr, permission)}</span>
                </label>
              );
            })}
          </fieldset>
        );
      })}
    </div>
  );
}
